import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { SummaryItem } from "../../../../../model/calculator-us-remastered/summaryItem";

@Component({
  selector: "app-calculator-summary",
  templateUrl: "./calculator-summary.component.html",
  styleUrls: ["./calculator-summary.component.scss"],
})
export class CalculatorSummaryComponent implements OnInit, OnChanges {
  @Input() summaryItems: SummaryItem[] = [];
  @Input() publisher: any;

  // Strukturierte Summary-Items nach Kategorien sortiert (von unten nach oben wie im Formular)
  structuredSummaryItems: SummaryItem[] = [];

  // Speichere die ursprünglichen Produktwerte beim ersten Laden
  hasInitializedProductValues = false;
  hasInitializedPublisher = false;

  constructor() {}

  ngOnInit(): void {
    this.updateStructuredSummary();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.summaryItems) {
      console.log("🔄 Summary items changed:", this.summaryItems);
      this.updateStructuredSummary();

      // Initialisiere die ursprünglichen Produktwerte nur beim ersten Mal
      if (
        !this.hasInitializedProductValues &&
        this.structuredSummaryItems.length > 0
      ) {
        console.log("⚙️ Initializing product values...");
        this.hasInitializedProductValues = true;
      }
    }
    if (changes.publisher) {
      console.log("🔄 Publisher changed:", this.publisher);
      if (this.publisher) {
        this.hasInitializedPublisher = true;
      }
    }
  }

  private updateStructuredSummary(): void {
    // Definiere die Reihenfolge der Felder entsprechend der Formular-Darstellung (von oben nach unten)
    const fieldOrder = [
      // Paper Specification (erstes Accordion im Formular)
      "bookPaper", // Paper Type (Book Paper)
      "bookPaperColor", // Paper Color
      "bookPaperBleeds", // Bleeds
      "endsheet", // Endsheet - non printed (Paper Type)

      // Case Type Specification (zweites Accordion im Formular)
      "caseType", // Select Case Type
      "boardMaterial", // Board Material (nur für 1-Piece/3-Piece)
      "caseMaterial", // Case Material (nur für 1-Piece/3-Piece)
      "caseColor", // Color (für alle Case Types)
      "embossing", // Embossing (nur für 1-Piece/3-Piece)
      "spineStamping", // Spine Stamping (nur für 1-Piece/3-Piece)
      "foil", // Foil (nur für 1-Piece/3-Piece)
      "frontBackStamping", // Front and Back Panel Stamping (nur für 1-Piece/3-Piece)
      "frontBackPanelFoil",

      // Case Wrap spezifische Felder
      "casePaper", // Case Paper (nur für Case Wrap)
      "finishing", // Finishing (nur für Case Wrap)
      "specialEffects", // Special Effects (nur für Case Wrap)
      "printOverFoil", // Print over Foil (nur für Case Wrap)

      // Conditional field für alle Case Types
      "dies", // Dies (nur für Initial/Reprint = Initial)

      // Component Specification:
      "selectedJacketPaper", // Jacket/Cover Paper
      "selectedFrontColor", // Front Color
      "selectedBackColor", // Back Color
      "selectedFinishing", // Finishing (Component version)
      "selectedSpecialEffects", // Special Effects (Component version)
      "selectedEndsheetPaper", // Endsheet Paper
      "selectedEndsheetFrontColor", // Endsheet Front Color
      "selectedEndsheetBackColor", // Endsheet Back Color

      // Prepress (kommt nach Component Specification)
      "prepPrint",
      "proofSoft",
      "componentPrepPrint",
      "componentProofSoft",

      // Bindery (kommt nach Prepress)
      "bind_style", // Bind Style
      "headband", // Headband
      "pack", // Pack
    ];

    // Definiere Insert-Pattern für dynamische Keys
    const insertPatterns = [
      /^insertPages_\d+$/, // insertPages_1, insertPages_2, etc.
      /^insertPaper_\d+$/, // insertPaper_1, insertPaper_2, etc.
      /^insertColors_\d+$/, // insertColors_1, insertColors_2, etc.
      /^insertSpecialInk_\d+$/, // insertSpecialInk_1, insertSpecialInk_2, etc.
      /^tipInPages_\d+$/, // tipInPages_1, tipInPages_2, etc.
      /^tipInPaper_\d+$/, // tipInPaper_1, tipInPaper_2, etc.
      /^tipInColors_\d+$/, // tipInColors_1, tipInColors_2, etc.
      /^tipInSpecialInk_\d+$/, // tipInSpecialInk_1, tipInSpecialInk_2, etc.
    ];

    // Filtere Items: entweder in fieldOrder oder passend zu Insert-Pattern
    const filteredItems = this.summaryItems.filter((item) => {
      // Prüfe ob Key in fieldOrder ist
      if (fieldOrder.includes(item.key)) {
        return true;
      }

      // Prüfe ob Key zu einem Insert-Pattern passt
      return insertPatterns.some((pattern) => pattern.test(item.key));
    });

    // Sortiere die gefilterten Summary-Items
    this.structuredSummaryItems = filteredItems.sort((a, b) => {
      const indexA = fieldOrder.indexOf(a.key);
      const indexB = fieldOrder.indexOf(b.key);

      // Wenn beide Items in fieldOrder sind, sortiere nach fieldOrder
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // Wenn nur A in fieldOrder ist, kommt A zuerst
      if (indexA !== -1 && indexB === -1) {
        return -1;
      }

      // Wenn nur B in fieldOrder ist, kommt B zuerst
      if (indexA === -1 && indexB !== -1) {
        return 1;
      }

      // Wenn beide Insert-Items sind, sortiere nach ID (Erstellungsreihenfolge) und dann nach Feld-Typ
      const getInsertId = (key: string) => {
        const match = key.match(/_(\d+)$/);
        return match ? parseInt(match[1]) : 0;
      };

      const idA = getInsertId(a.key);
      const idB = getInsertId(b.key);

      // Primär nach ID sortieren (Erstellungsreihenfolge)
      if (idA !== idB) {
        return idA - idB;
      }

      // Bei gleicher ID: sortiere nach Feld-Typ (Pages, Paper, Colors, Special Ink)
      const insertFieldOrder = ["Pages", "Paper", "Colors", "Special Ink"];
      const fieldIndexA = insertFieldOrder.indexOf(a.label);
      const fieldIndexB = insertFieldOrder.indexOf(b.label);

      return fieldIndexA - fieldIndexB;
    });
  }

  /**
   * Gibt den Display-Wert für ein Item zurück, mit Behandlung für deaktivierte Werte
   */
  getDisplayValue(item: SummaryItem): string {
    if (item.currentValue === "DEACTIVATED_BY_PRINTED_ENDSHEET") {
      return item.defaultValue || "Not selected";
    }
    return item.currentValue || "Not selected";
  }

  /**
   * Gruppiert Items nach Kategorie für bessere Darstellung
   */
  getItemsByCategory(): { [category: string]: SummaryItem[] } {
    const grouped: { [category: string]: SummaryItem[] } = {};

    this.structuredSummaryItems.forEach((item) => {
      item.uniqueLabel = String(item.category + "-" + item.label)
        .replace(/\s+/g, "_")
        .replace(/&/g, "and")
        .toLowerCase();
      if (!grouped[item.category]) {
        grouped[item.category] = [];
      }
      grouped[item.category].push(item);
    });

    return grouped;
  }

  /**
   * Liefert die Kategorien in der gewünschten Reihenfolge
   */
  getCategoriesInOrder(): string[] {
    const allCategories = Object.keys(this.getItemsByCategory());

    const baseOrder = ["Paper Specification", "Case Type Specification"];

    // Component Specification Subkategorien dynamisch sortieren
    const componentCategories = allCategories
      .filter((cat) => cat.startsWith("Component Specification"))
      .sort((a, b) => {
        // Sortierreihenfolge: Jacket/Cover → Endsheets → Insert → Tip In
        const order = ["Jacket", "Cover", "Endsheet", "Insert", "Tip In"];

        const aType = order.find((type) => a.includes(type)) || "zz";
        const bType = order.find((type) => b.includes(type)) || "zz";

        const aIndex = order.indexOf(aType);
        const bIndex = order.indexOf(bType);

        if (aIndex !== bIndex) {
          return aIndex - bIndex;
        }

        // Bei gleicher Hauptkategorie alphabetisch sortieren
        return a.localeCompare(b);
      });

    // Nach Component Specifications kommen Prepress und Bindery
    const postComponentOrder = ["Prepress", "Bindery"];

    // Kombiniere alle Orders: Base → Component → Post-Component
    const finalOrder = [
      ...baseOrder,
      ...componentCategories,
      ...postComponentOrder,
    ];

    // Filtere nur vorhandene Kategorien in der gewünschten Reihenfolge
    return finalOrder.filter((category) => allCategories.includes(category));
  }
}
